/**
 * Adapter factory — v10.10.0
 * Selecciona el adaptador correcto según el device_type declarado por el
 * cliente al vincular la sesión.
 *
 * Cambios v10.10.0:
 *  - `mixed_reality` ahora usa `MetaQuestAdapter` REAL (WebXR immersive-ar +
 *    hand-tracking + hit-test + anchors). Fallback a WebPWAAdapter si
 *    WebXR no está soportado en el visor.
 */
import { XRDeviceAdapter } from './XRDeviceAdapter';
import { WebPWAAdapter } from './WebPWAAdapter';
import { MetaQuestAdapter } from './MetaQuestAdapter';

// XREAL y AR Foundation permanecen como stubs (Fase 3/4)
class XREALAdapterStub extends XRDeviceAdapter {
  static get id() { return 'xreal_stub'; }
  async capabilities() {
    return {
      camera_supported: true,
      marker_tracking_supported: true,
      webxr_supported: true,
      depth_supported: false,
      spatial_anchor_supported: true,
      hand_tracking_supported: false,
      voice_supported: false,
      _stub: true,
    };
  }
  async startMarkerTracking() {
    throw new Error('XREALAdapter no implementado todavía — Fase 4');
  }
}

class ARFoundationAdapterStub extends XRDeviceAdapter {
  static get id() { return 'ar_foundation_stub'; }
  async capabilities() {
    return {
      camera_supported: true,
      marker_tracking_supported: true,
      webxr_supported: false,
      depth_supported: true,
      spatial_anchor_supported: true,
      hand_tracking_supported: false,
      voice_supported: false,
      _stub: true,
    };
  }
}

const REGISTRY = {
  mobile_ar: WebPWAAdapter,
  tablet_ar: WebPWAAdapter,
  mixed_reality: MetaQuestAdapter, // v10.10.0 — implementación real
  ar_glasses: XREALAdapterStub,
  ar_foundation: ARFoundationAdapterStub,
};

/**
 * Crea el adaptador para `deviceType`. Si es `mixed_reality` pero
 * WebXR no está soportado en el navegador (ej. abrir el link en un móvil
 * en vez de en el Quest), devuelve el WebPWAAdapter para no romper el
 * flujo — el llamador puede detectar `adapter.constructor.id === 'web_pwa'`
 * para mostrar un aviso de "modo fallback".
 */
export function createAdapter(deviceType, opts = {}) {
  const Cls = REGISTRY[deviceType] || WebPWAAdapter;
  // Fallback dinámico: si es Meta Quest pero no hay WebXR, caemos a PWA
  if (Cls === MetaQuestAdapter) {
    const hasXR = typeof navigator !== 'undefined' && !!navigator.xr;
    if (!hasXR) return new WebPWAAdapter({ deviceType, ...opts, _fellBackFrom: 'mixed_reality' });
  }
  return new Cls({ deviceType, ...opts });
}

export { WebPWAAdapter, MetaQuestAdapter, XREALAdapterStub, ARFoundationAdapterStub };
